import React, { PropsWithChildren, createContext, useState, useCallback, useMemo } from 'react';
import type { Currency } from '../types';

export const SUPPORTED_CURRENCIES: readonly Currency[] = ['DZD', 'EUR', 'USD', 'GBP', 'CHF', 'MAD', 'TND'];

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  DZD: 'د.ج',
  EUR: '€',
  USD: '$',
  GBP: '£',
  CHF: 'CHF',
  MAD: 'د.م.',
  TND: 'د.ت',
};

export const CURRENCY_NAMES: Record<Currency, string> = {
  DZD: 'Algerian Dinar',
  EUR: 'Euro',
  USD: 'US Dollar',
  GBP: 'British Pound',
  CHF: 'Swiss Franc',
  MAD: 'Moroccan Dirham',
  TND: 'Tunisian Dinar',
};

type CurrencyContextType = {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  currencySymbol: string;
  currencyName: string;
  supportedCurrencies: readonly Currency[];
  currencySymbols: Record<Currency, string>;
  currencyNames: Record<Currency, string>;
};

export const CurrencyContext = createContext<CurrencyContextType>({} as CurrencyContextType);

export const CurrencyContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [currency, setCurrencyState] = useState<Currency>(() => {
    // Initialize from localStorage or default to DZD
    try {
      const savedCurrency = localStorage.getItem('selectedCurrency');
      if (savedCurrency && SUPPORTED_CURRENCIES.includes(savedCurrency as Currency)) {
        return savedCurrency as Currency;
      }
    } catch {
      // localStorage may not be available
    }
    return 'DZD';
  });

  const setCurrency = useCallback((newCurrency: Currency) => {
    if (!SUPPORTED_CURRENCIES.includes(newCurrency)) {
      return;
    }

    setCurrencyState(newCurrency);

    // Persist to localStorage
    try {
      localStorage.setItem('selectedCurrency', newCurrency);
    } catch {
      // localStorage may not be available
    }
  }, []);

  const currencySymbol = useMemo(() => CURRENCY_SYMBOLS[currency], [currency]);
  const currencyName = useMemo(() => CURRENCY_NAMES[currency], [currency]);

  const value = useMemo(
    () => ({
      currency,
      setCurrency,
      currencySymbol,
      currencyName,
      supportedCurrencies: SUPPORTED_CURRENCIES,
      currencySymbols: CURRENCY_SYMBOLS,
      currencyNames: CURRENCY_NAMES,
    }),
    [currency, setCurrency, currencySymbol, currencyName]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};
